import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logoWithoutMoto from '../assets/logowithoutmoto.jpg';

const words = [
    { char: 'R', full: 'Reliable' },
    { char: 'A', full: 'Asset' },
    { char: 'M', full: 'Management &' },
    { char: 'B', full: 'Business' },
];

export default function LoadingScreen({ onComplete }) {
    const [activeIdx, setActiveIdx] = useState(0);
    const [stage, setStage] = useState('sequence');  // 'sequence' -> 'logo' -> 'done'

    useEffect(() => {
        document.body.style.overflow = 'hidden';

        // SLOWER TIMING:
        // Increased interval to 1.5s per word for slower pace
        const interval = setInterval(() => {
            setActiveIdx((prev) => {
                if (prev < words.length) return prev + 1;
                return prev;
            });
        }, 1500);

        // Show Logo after ~6s (4 words * 1.5s = 6s)
        const timer1 = setTimeout(() => {
            setStage('logo');
            clearInterval(interval);
        }, 6500);

        // Done after ~11s
        const timer2 = setTimeout(() => {
            setStage('done');
            onComplete();
            document.body.style.overflow = '';
        }, 11500);

        return () => {
            clearInterval(interval);
            clearTimeout(timer1);
            clearTimeout(timer2);
            document.body.style.overflow = '';
        };
    }, [onComplete]);

    if (stage === 'done') return null;

    const brandColor = 'rgba(69, 195, 235, 1)';

    return (
        <motion.div
            className="loading-screen"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }} // Slower exit
            style={{
                position: 'fixed',
                inset: 0,
                background: '#ffffff',
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <AnimatePresence mode="wait">

                {/* PHASE 1: SEQUENTIAL ACROYNM FORMATION */}
                {(stage === 'sequence') && (
                    <motion.div
                        key="text-sequence"
                        exit={{ opacity: 0, scale: 0.95, filter: 'blur(10px)' }}
                        transition={{ duration: 0.8 }} // Slower fade out
                        className="loading-row-container"
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '12px',
                        }}
                    >
                        {words.map((item, i) => (
                            <WordBlock
                                key={i}
                                item={item}
                                index={i}
                                activeIndex={activeIdx}
                                color={brandColor}
                            />
                        ))}
                    </motion.div>
                )}

                {/* PHASE 2: LOGO & MOTTO */}
                {stage === 'logo' && (
                    <motion.div
                        key="logo-container"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1, ease: "easeOut" }} // Slower logo reveal
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center'
                        }}
                    >
                        <motion.img
                            src={logoWithoutMoto}
                            alt="RAMB Logo"
                            className="loading-logo-img"
                            initial={{ y: 30, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 1.2 }}
                        />

                        {/* Horizontal reveal for Motto */}
                        <motion.div
                            initial={{ width: 0, opacity: 0 }}
                            animate={{ width: 'auto', opacity: 1 }}
                            transition={{ delay: 1, duration: 2, ease: "easeInOut" }} // Slower reveal
                            className="loading-motto"
                        >
                            <motion.span className="loading-motto-text" style={{ color: brandColor }}>
                                Your success is our interest
                            </motion.span>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}

function WordBlock({ item, index, activeIndex, color }) {
    if (index > activeIndex) return null;

    const isActive = index === activeIndex;
    const char = item.char;
    const rest = item.full.substring(1);

    return (
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <motion.span
                className="loading-char loading-char-text"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }} // Slower letter appear
                style={{ color: color }}
            >
                {char}
            </motion.span>

            <motion.span
                className="loading-word"
                initial={{ width: 0, opacity: 0 }}
                animate={{
                    width: isActive ? 'auto' : 0,
                    opacity: isActive ? 1 : 0,
                    marginLeft: isActive ? '4px' : 0
                }}
                transition={{
                    width: { duration: 0.8, ease: "circOut" }, // Slower expand
                    opacity: { duration: 0.6 },
                }}
            >
                {rest}
            </motion.span>
        </div>
    );
}
